/**
 * The design's sign-your-contract block (COL-533).
 *
 * V2 used to link back to V1 for this — "Signing still happens in the
 * previous view." The export draws it inline instead: a confirmation
 * checkbox, then a "Sign digitally" button that stays disabled until it is
 * ticked.
 *
 * Everything it does goes through the modules shared with V1 — the four
 * edge-function calls in `@/lib/docusign-contract`, the date-of-birth rules
 * in `@/lib/contract-dob-gate`. Only the presentation is new: a tenant must
 * not be able to reach DocuSign through a path with weaker gates than the
 * one V1 already enforces.
 *
 * The gates, in order, exactly as V1 runs them:
 *   1. the checkbox        — the design's own addition
 *   2. date of birth       — COL-258; it prints on the contract
 *   3. an explicit confirm — before leaving for DocuSign
 */

import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { supabase } from "@/lib/supabase";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { latestAdultBirthDate } from "@/lib/adult-age";
import { checkDateOfBirth, persistDateOfBirth } from "@/lib/contract-dob-gate";
import { syncDocusignEnvelope } from "@/lib/docusign-contract";
import {
  getDocusignSigningUrl,
  openSigningWindow,
  sendContractToDocusign,
} from "@/lib/docusign-contract";

type Labels = {
  readConfirm: string;
  signDigitally: string;
  openingDocusign: string;
  openingThisTab: string;
  confirmSignTitle: string;
  confirmSignBody: string;
  confirmSignCta: string;
  cancel: string;
  dobTitle: string;
  dobBody: string;
  dobLabel: string;
  dobSave: string;
  dobEmpty: string;
  dobInvalid: string;
  dobUnderage: string;
  signFailed: string;
};

type Props = {
  bookingId: number;
  /** Whether an envelope already exists — decides which call opens it. */
  hasEnvelope: boolean;
  labels: Labels;
};

export default function ContractSignBlock({
  bookingId,
  hasEnvelope,
  labels,
}: Props) {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();

  /*
    Read on the SAME key V1 writes and invalidates (`["user-profile", id]`),
    so a date saved on either dashboard is immediately current on the other
    rather than each holding its own stale copy.
  */
  const { data: profile } = useQuery({
    queryKey: ["user-profile", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("name, email, phone, date_of_birth")
        .eq("id", user.id)
        .single();
      if (error) throw error;
      return data as unknown as { date_of_birth: string | null };
    },
  });
  const dateOfBirth = profile?.date_of_birth ?? null;

  const [agreed, setAgreed] = useState(false);
  const [dobOpen, setDobOpen] = useState(false);
  const [dobInput, setDobInput] = useState("");
  const [savingDob, setSavingDob] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [opening, setOpening] = useState(false);
  /*
    Set the moment a send succeeds, and never cleared for the life of this
    mount.

    `hasEnvelope` is derived from the booking row, which does not refetch
    between a send and the tenant pressing the button again. Without this a
    second press takes the SEND path a second time — and that is destructive,
    not idempotent: `send-contract-to-docusign` regenerates the PDF through
    `create-contract` BEFORE its own ALREADY_SENT guard, `create-contract`
    voids the existing envelope and nulls `docusign_envelope_id` for exactly
    the statuses this block renders for (`envelope_draft`, `ready_to_sign`),
    so the guard then sees null and mints a replacement. The tenant's first
    signing link — already in their inbox — silently stops working.

    V1 avoided this with its `signingUrlJustSent` / `justSentToDocuSign`
    state; this is the same idea at this surface's altitude.
  */
  const [envelopeSent, setEnvelopeSent] = useState(false);

  /*
    Re-check DocuSign on mount, as V1 does.

    The booking row is cached, so when an admin regenerates a contract the
    tenant keeps looking at the previous envelope state until they reload --
    and the sign button acts on it. V1 runs exactly this sync and
    invalidates on `updated`; V2 dropped it, which is why signing could be
    stuck until a manual refresh.

    Once per booking per mount, and the ref is cleared on failure so a
    transient error does not disable the re-check for the rest of the
    session. Failures are otherwise silent: this is a background
    reconciliation, not something the tenant asked for.
  */
  const syncedForBooking = useRef<number | null>(null);
  useEffect(() => {
    if (!hasEnvelope) return;
    if (!Number.isFinite(bookingId)) return;
    if (syncedForBooking.current === bookingId) return;
    syncedForBooking.current = bookingId;
    syncDocusignEnvelope(bookingId, labels.signFailed)
      .then((res: any) => {
        if (res?.updated) {
          queryClient.invalidateQueries({ queryKey: ["active-booking"] });
        }
      })
      .catch(() => {
        syncedForBooking.current = null;
      });
  }, [bookingId, hasEnvelope, labels.signFailed, queryClient]);

  const startGate = () => {
    setDobInput(dateOfBirth ?? "");
    setDobOpen(true);
  };

  const submitDob = async () => {
    const checked = checkDateOfBirth(dobInput);
    if (!checked.ok) {
      toast.error(
        checked.reason === "empty"
          ? labels.dobEmpty
          : checked.reason === "underage"
            ? labels.dobUnderage
            : labels.dobInvalid
      );
      return;
    }
    if (!user?.id) return;
    setSavingDob(true);
    try {
      if (await persistDateOfBirth(user.id, checked.value, dateOfBirth)) {
        queryClient.invalidateQueries({ queryKey: ["user-profile", user.id] });
      }
      setDobOpen(false);
      setConfirmOpen(true);
    } catch (e: any) {
      toast.error(e?.message || labels.signFailed);
    } finally {
      setSavingDob(false);
    }
  };

  const openSigning = async () => {
    setOpening(true);
    try {
      /*
        An envelope that already exists is re-opened rather than re-sent:
        `send-contract-to-docusign` would create a second envelope for the
        same booking, which is how a tenant ends up with two contracts to
        sign and we end up not knowing which one counts.
      */
      /*
        Read the envelope state fresh before choosing the path.

        `hasEnvelope` comes from a cached booking row and `envelopeSent` is
        per-mount, so a second tab -- or this tab after an admin acted --
        could still believe no envelope exists and take the SEND path. That
        path is destructive: send-contract-to-docusign regenerates through
        create-contract BEFORE its own ALREADY_SENT guard, voiding the
        envelope whose link is already in the tenant's inbox.

        This narrows the window to a true race rather than closing it; the
        real fix is to make the server check before it regenerates, which is
        an edge-function change on the contract path and wants its own
        review.

        The read can only ever turn the guard ON, never off -- note the OR.
        Letting a read that answers "no envelope" overrule a cached yes
        would ROUTE INTO the destructive path on a bad or stale read, which
        is precisely the outcome this guard exists to prevent. A failure
        reading is likewise treated as "no new information".
      */
      let freshEnvelope = false;
      try {
        const { data: fresh } = await (supabase as any)
          .from("bookings")
          .select("docusign_envelope_id")
          .eq("id", bookingId)
          .maybeSingle();
        freshEnvelope = !!fresh?.docusign_envelope_id;
      } catch {
        // No new information; fall through on what we already knew.
      }
      const alreadyHasEnvelope = hasEnvelope || envelopeSent || freshEnvelope;
      const data = alreadyHasEnvelope
        ? await getDocusignSigningUrl(bookingId, labels.signFailed)
        : await sendContractToDocusign(bookingId, labels.signFailed);
      if (!alreadyHasEnvelope) {
        // Record it before anything else can fail: a send that succeeded
        // server-side must never be repeated, even if opening the tab does.
        setEnvelopeSent(true);
        queryClient.invalidateQueries({ queryKey: ["active-booking"] });
      }
      const url = typeof data?.url === "string" ? data.url : "";
      if (!url) {
        toast.error(labels.signFailed);
        return;
      }
      openSigningWindow(url, {
        isMobile,
        onSameTab: (reason) =>
          toast.info(
            reason === "mobile" ? labels.openingDocusign : labels.openingThisTab
          ),
      });
    } catch (e: any) {
      // The edge functions put the actionable half in `details`.
      toast.error(e?.message || labels.signFailed, {
        description: e?.details,
      });
    } finally {
      setOpening(false);
    }
  };

  return (
    <>
      <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-border/60 pt-6">
        <label className="flex cursor-pointer items-center gap-3 text-sm text-foreground">
          <Checkbox
            checked={agreed}
            onCheckedChange={(v) => setAgreed(v === true)}
          />
          <span>{labels.readConfirm}</span>
        </label>
        <Button
          className="rounded-full px-6"
          disabled={!agreed || opening}
          onClick={startGate}
        >
          {labels.signDigitally}
        </Button>
      </div>

      <Dialog open={dobOpen} onOpenChange={setDobOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{labels.dobTitle}</DialogTitle>
            <DialogDescription>{labels.dobBody}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="v2-dob">{labels.dobLabel}</Label>
            <Input
              id="v2-dob"
              type="date"
              value={dobInput}
              max={latestAdultBirthDate()}
              onChange={(e) => setDobInput(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDobOpen(false)}>
              {labels.cancel}
            </Button>
            <Button onClick={() => void submitDob()} disabled={savingDob}>
              {labels.dobSave}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{labels.confirmSignTitle}</DialogTitle>
            <DialogDescription>{labels.confirmSignBody}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              {labels.cancel}
            </Button>
            <Button
              disabled={opening}
              onClick={() => {
                setConfirmOpen(false);
                // Let the dialog portal unmount commit first; V1 does the
                // same, against rare React insertBefore errors.
                setTimeout(() => void openSigning(), 0);
              }}
            >
              {labels.confirmSignCta}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
